import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";

import {
  RiskManagementOverallRiskBarChartsDocument,
  type RiskManagementOverallRiskBarChartsQuery,
  type RiskManagementOverallRiskBarChartsQueryVariables,
} from "$/__generated__/graphql";

import {
  appendRiskManagementSubgroups,
  appendOverallRiskTooltipExtras,
  convertOverallBarDataToSeries,
  filterNullishOPINTotals,
  filterNullishThreatTotals,
} from "./utils";
import { useGlobalFiltersContext } from "contexts/filters/global-filters/context";
import {
  type GeneralBarDatum,
  storageFormatters,
} from "@jana-technology/platform-ui";
import { usePublishedRiskUnitFormatter } from "../usePublishedRiskUnitFormatter";

export const useOverallRiskCharts = (
  options: useQuery.Options<
    RiskManagementOverallRiskBarChartsQuery,
    RiskManagementOverallRiskBarChartsQueryVariables
  >,
) => {
  const overallRiskChartsQueryResult = useQuery(
    RiskManagementOverallRiskBarChartsDocument,
    options,
  );
  const riskUnitFormatter = usePublishedRiskUnitFormatter();

  const {
    appliedWellName,
    appliedHierarchyName,
    overallOperationalRiskByWellName,
    overallInterventionRiskByWellName,
  } = useGlobalFiltersContext();

  const {
    overallRiskByThreatSeries,
    overallRiskByConsequenceSeries,
    overallRiskByOutcomeScenarioSeries,
    overallRiskDataKeys,
  } = useMemo(() => {
    let overallRiskByThreatSeries: Array<GeneralBarDatum> = [];
    let overallRiskByConsequenceSeries: Array<GeneralBarDatum> = [];
    let overallRiskByOutcomeScenarioSeries: Array<GeneralBarDatum> = [];

    if (overallRiskChartsQueryResult.data?.riskManagementChartsForWellNode) {
      const {
        overallRiskByThreat,
        overallRiskByConsequence,
        overallRiskByOutcomeScenario,
      } = overallRiskChartsQueryResult.data.riskManagementChartsForWellNode;

      overallRiskByThreatSeries = convertOverallBarDataToSeries(
        filterNullishThreatTotals(overallRiskByThreat),
      );

      overallRiskByConsequenceSeries = convertOverallBarDataToSeries(
        filterNullishOPINTotals(overallRiskByConsequence),
      );

      overallRiskByOutcomeScenarioSeries = convertOverallBarDataToSeries(
        filterNullishOPINTotals(overallRiskByOutcomeScenario),
      );

      const tooltipTitle = appliedWellName
        ? `${appliedWellName} (${appliedHierarchyName})`
        : "";

      for (const threatDatum of overallRiskByThreatSeries) {
        threatDatum.title = tooltipTitle;
        threatDatum.formatter = riskUnitFormatter;
        appendOverallRiskTooltipExtras(
          overallOperationalRiskByWellName,
          overallInterventionRiskByWellName,
          threatDatum,
          riskUnitFormatter,
        );
      }

      for (let i = 0; i < overallRiskByConsequenceSeries.length; i++) {
        overallRiskByConsequenceSeries[i].title = tooltipTitle;
        overallRiskByConsequenceSeries[i].formatter = riskUnitFormatter;
        appendRiskManagementSubgroups(
          [overallRiskByConsequence[i]],
          overallRiskByConsequenceSeries[i],
          "By Category",
          riskUnitFormatter,
        );

        appendOverallRiskTooltipExtras(
          overallOperationalRiskByWellName,
          overallInterventionRiskByWellName,
          overallRiskByConsequenceSeries[i],
          riskUnitFormatter,
        );
      }

      for (let i = 0; i < overallRiskByOutcomeScenarioSeries.length; i++) {
        overallRiskByOutcomeScenarioSeries[i].title = tooltipTitle;
        overallRiskByOutcomeScenarioSeries[i].formatter = riskUnitFormatter;
        overallRiskByOutcomeScenarioSeries[i].tooltipExtras = [
          {
            label: `LOF (${overallRiskByOutcomeScenario[i].group})`,
            value: storageFormatters.scientificNotation(
              overallRiskByOutcomeScenario[i]?.opLof ?? 0,
            ),
            subGroup: "Operational Risk",
          },
          {
            label: `LOF (${overallRiskByOutcomeScenario[i].group})`,
            value: storageFormatters.scientificNotation(
              overallRiskByOutcomeScenario[i]?.inLof ?? 0,
            ),
            subGroup: "Intervention Risk",
          },
          {
            label: `COF (${overallRiskByOutcomeScenario[i].group})`,
            value: riskUnitFormatter(
              overallRiskByOutcomeScenario[i]?.opCof ?? 0,
            ),
            subGroup: "Operational Risk",
          },
          {
            label: `COF (${overallRiskByOutcomeScenario[i].group})`,
            value: riskUnitFormatter(
              overallRiskByOutcomeScenario[i]?.inCof ?? 0,
            ),
            subGroup: "Intervention Risk",
          },
        ];

        appendOverallRiskTooltipExtras(
          overallOperationalRiskByWellName,
          overallInterventionRiskByWellName,
          overallRiskByOutcomeScenarioSeries[i],
          riskUnitFormatter,
        );
      }

      return {
        overallRiskByThreatSeries,
        overallRiskByConsequenceSeries,
        overallRiskByOutcomeScenarioSeries,
        overallRiskDataKeys: ["Operational Risk", "Intervention Risk"],
      };
    }

    return {
      overallRiskByThreatSeries: [],
      overallRiskByConsequenceSeries: [],
      overallRiskByOutcomeScenarioSeries: [],
      overallRiskDataKeys: [],
    };
  }, [
    overallRiskChartsQueryResult.data?.riskManagementChartsForWellNode,
    overallOperationalRiskByWellName,
    overallInterventionRiskByWellName,
    appliedWellName,
    appliedHierarchyName,
    riskUnitFormatter,
  ]);

  return {
    overallRiskChartsQueryResult,
    overallRiskByThreatSeries,
    overallRiskByConsequenceSeries,
    overallRiskByOutcomeScenarioSeries,
    overallRiskDataKeys,
  };
};
